import { useRef, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { FileVideo, Loader2, Upload, UserPlus, X } from "lucide-react";
import { toast } from "sonner";
import { useLang } from "@/i18n";
import { CITIES, POSITIONS, LEAGUE_PREFERENCES, SCOUTING_ORIGINS } from "@/constants/markets";

const EMPTY_FORM = {
  name: "",
  city: "",
  position: "",
  style: "",
  commitment: "",
  pace: "",
  defense: "",
  passing: "",
  videoUrl: "",
  league: "Open",
  origin: "none",
};

const isValidStat = (v) => {
  const n = Number(v);
  return Number.isFinite(n) && n >= 1 && n <= 99;
};

const formatSize = (bytes) => {
  if (!bytes && bytes !== 0) return "";
  if (bytes < 1024 * 1024) return `${Math.max(1, Math.round(bytes / 1024))} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

export const RegisterModal = ({ open, onClose, onRegister }) => {
  const { t } = useLang();
  const [form, setForm] = useState(EMPTY_FORM);
  const [videoFile, setVideoFile] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [error, setError] = useState("");
  const fileInputRef = useRef(null);

  const set = (field) => (value) => setForm((f) => ({ ...f, [field]: value }));
  const setInput = (field) => (e) => set(field)(e.target.value);

  const handleClose = () => {
    if (submitting || uploading) return;
    setForm(EMPTY_FORM);
    setVideoFile(null);
    setUploadProgress(0);
    setError("");
    onClose();
  };

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (file) setVideoFile(file);
    // allow selecting the same file again after removal
    e.target.value = "";
  };

  const removeFile = () => {
    setVideoFile(null);
    setUploadProgress(0);
  };

  // MVP: simulate a brief upload progress before completing registration
  const simulateUpload = () =>
    new Promise((resolve) => {
      setUploading(true);
      setUploadProgress(0);
      let p = 0;
      const iv = setInterval(() => {
        p += 9 + Math.random() * 12;
        if (p >= 100) {
          setUploadProgress(100);
          clearInterval(iv);
          setTimeout(resolve, 250);
        } else {
          setUploadProgress(Math.round(p));
        }
      }, 110);
    });

  const handleSubmit = async (e) => {
    e.preventDefault();
    const valid =
      form.name.trim() &&
      form.city &&
      form.position &&
      form.style.trim() &&
      form.commitment.trim() &&
      isValidStat(form.pace) &&
      isValidStat(form.defense) &&
      isValidStat(form.passing);
    if (!valid) {
      setError(t("formError"));
      return;
    }
    setError("");
    setSubmitting(true);
    try {
      if (videoFile) {
        await simulateUpload();
        setUploading(false);
      }
      await onRegister({
        name: form.name.trim(),
        city: form.city,
        position: form.position,
        style: form.style.trim(),
        commitment: form.commitment.trim(),
        stats: {
          pace: Number(form.pace),
          defense: Number(form.defense),
          passing: Number(form.passing),
        },
        videoUrl: form.videoUrl.trim() || null,
        videoHighlight: !!videoFile,
        leaguePreference: form.league,
        scoutingOrigin: form.origin === "none" ? null : form.origin,
      });
      setForm(EMPTY_FORM);
      setVideoFile(null);
      setUploadProgress(0);
      onClose();
    } catch (err) {
      console.error("Registration failed", err);
      toast.error(t("toastRegisterError"));
    } finally {
      setUploading(false);
      setSubmitting(false);
    }
  };

  const statField = (field, label, testId) => (
    <div className="flex flex-col gap-1.5">
      <Label htmlFor={`reg-${field}`} className="text-xs uppercase tracking-[0.1em] font-semibold text-[hsl(var(--muted-foreground))]">
        {label}
      </Label>
      <Input
        id={`reg-${field}`}
        data-testid={testId}
        type="number"
        min={1}
        max={99}
        value={form[field]}
        onChange={setInput(field)}
        placeholder="1-99"
        className="h-10 bg-[hsl(var(--surface))] border-[hsl(var(--input))] focus-visible:ring-2 focus-visible:ring-[hsl(var(--news-red))]"
      />
    </div>
  );

  return (
    <Dialog open={open} onOpenChange={(o) => !o && handleClose()}>
      <DialogContent
        data-testid="register-modal"
        className="max-w-lg bg-[hsl(var(--surface))] border border-[hsl(var(--border))] p-0 overflow-hidden gap-0 max-h-[90vh] overflow-y-auto"
      >
        <div className="h-[6px] w-full bg-[hsl(var(--navy))]" />
        <div className="px-6 sm:px-8 pt-6 pb-7">
          <div className="flex items-center gap-3 mb-1">
            <div className="h-10 w-10 rounded-lg bg-[hsl(var(--navy))] text-white flex items-center justify-center shrink-0">
              <UserPlus className="h-5 w-5" />
            </div>
            <div>
              <DialogTitle className="font-display uppercase text-2xl sm:text-3xl leading-none text-[hsl(var(--navy))] tracking-[0.04em]">
                {t("registerTitle")}
              </DialogTitle>
              <DialogDescription className="text-sm text-[hsl(var(--muted-foreground))] mt-1">
                {t("registerDesc")}
              </DialogDescription>
            </div>
          </div>

          <form onSubmit={handleSubmit} className="mt-5 space-y-4">
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="reg-name" className="text-xs uppercase tracking-[0.1em] font-semibold text-[hsl(var(--muted-foreground))]">
                {t("nameLabel")}
              </Label>
              <Input
                id="reg-name"
                data-testid="register-name-input"
                value={form.name}
                onChange={setInput("name")}
                placeholder={t("namePlaceholder")}
                maxLength={80}
                className="h-10 bg-[hsl(var(--surface))] border-[hsl(var(--input))] focus-visible:ring-2 focus-visible:ring-[hsl(var(--news-red))]"
              />
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="flex flex-col gap-1.5">
                <Label className="text-xs uppercase tracking-[0.1em] font-semibold text-[hsl(var(--muted-foreground))]">
                  {t("cityLabel")}
                </Label>
                <Select value={form.city} onValueChange={set("city")}>
                  <SelectTrigger
                    data-testid="register-city-select"
                    className="h-10 bg-[hsl(var(--surface))] border-[hsl(var(--input))] focus:ring-2 focus:ring-[hsl(var(--news-red))]"
                  >
                    <SelectValue placeholder={t("selectCity")} />
                  </SelectTrigger>
                  <SelectContent className="bg-[hsl(var(--popover))]">
                    {CITIES.map((c) => (
                      <SelectItem key={c} value={c}>
                        {c}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="flex flex-col gap-1.5">
                <Label className="text-xs uppercase tracking-[0.1em] font-semibold text-[hsl(var(--muted-foreground))]">
                  {t("position")}
                </Label>
                <Select value={form.position} onValueChange={set("position")}>
                  <SelectTrigger
                    data-testid="register-position-select"
                    className="h-10 bg-[hsl(var(--surface))] border-[hsl(var(--input))] focus:ring-2 focus:ring-[hsl(var(--news-red))]"
                  >
                    <SelectValue placeholder={t("selectPosition")} />
                  </SelectTrigger>
                  <SelectContent className="bg-[hsl(var(--popover))]">
                    {POSITIONS.map((p) => (
                      <SelectItem key={p} value={p}>
                        {p}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="flex flex-col gap-1.5">
                <Label htmlFor="reg-style" className="text-xs uppercase tracking-[0.1em] font-semibold text-[hsl(var(--muted-foreground))]">
                  {t("style")}
                </Label>
                <Input
                  id="reg-style"
                  data-testid="register-style-input"
                  value={form.style}
                  onChange={setInput("style")}
                  placeholder={t("stylePlaceholder")}
                  maxLength={80}
                  className="h-10 bg-[hsl(var(--surface))] border-[hsl(var(--input))] focus-visible:ring-2 focus-visible:ring-[hsl(var(--news-red))]"
                />
              </div>
              <div className="flex flex-col gap-1.5">
                <Label htmlFor="reg-commitment" className="text-xs uppercase tracking-[0.1em] font-semibold text-[hsl(var(--muted-foreground))]">
                  {t("commitment")}
                </Label>
                <Input
                  id="reg-commitment"
                  data-testid="register-commitment-input"
                  value={form.commitment}
                  onChange={setInput("commitment")}
                  placeholder={t("commitmentPlaceholder")}
                  maxLength={80}
                  className="h-10 bg-[hsl(var(--surface))] border-[hsl(var(--input))] focus-visible:ring-2 focus-visible:ring-[hsl(var(--news-red))]"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="flex flex-col gap-1.5">
                <Label className="text-xs uppercase tracking-[0.1em] font-semibold text-[hsl(var(--muted-foreground))]">
                  {t("leagueType")}
                </Label>
                <Select value={form.league} onValueChange={set("league")}>
                  <SelectTrigger
                    data-testid="register-league-select"
                    className="h-10 bg-[hsl(var(--surface))] border-[hsl(var(--input))] focus:ring-2 focus:ring-[hsl(var(--news-red))]"
                  >
                    <SelectValue placeholder={t("selectLeague")} />
                  </SelectTrigger>
                  <SelectContent className="bg-[hsl(var(--popover))]">
                    {LEAGUE_PREFERENCES.map((l) => (
                      <SelectItem key={l} value={l}>
                        {l}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="flex flex-col gap-1.5">
                <Label className="text-xs uppercase tracking-[0.1em] font-semibold text-[hsl(var(--muted-foreground))]">
                  {t("scoutingOrigin")}{" "}
                  <span className="normal-case font-normal">{t("optionalTag")}</span>
                </Label>
                <Select value={form.origin} onValueChange={set("origin")}>
                  <SelectTrigger
                    data-testid="register-origin-select"
                    className="h-10 bg-[hsl(var(--surface))] border-[hsl(var(--input))] focus:ring-2 focus:ring-[hsl(var(--news-red))]"
                  >
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-[hsl(var(--popover))]">
                    <SelectItem value="none">{t("originNone")}</SelectItem>
                    {SCOUTING_ORIGINS.map((o) => (
                      <SelectItem key={o} value={o}>
                        {o}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-3 gap-3">
              {statField("pace", t("pace"), "register-pace-input")}
              {statField("defense", t("defense"), "register-defense-input")}
              {statField("passing", t("passing"), "register-passing-input")}
            </div>

            {/* Pro-pathway: native video upload dropzone */}
            <div className="flex flex-col gap-1.5">
              <Label className="text-xs uppercase tracking-[0.1em] font-semibold text-[hsl(var(--muted-foreground))]">
                🎬 {t("videoLabel")}{" "}
                <span className="normal-case font-normal">{t("optionalTag")}</span>
              </Label>
              <input
                ref={fileInputRef}
                data-testid="register-video-file-input"
                type="file"
                accept="video/*"
                onChange={handleFileChange}
                className="hidden"
              />
              {!videoFile ? (
                <button
                  type="button"
                  data-testid="register-upload-zone"
                  onClick={() => fileInputRef.current?.click()}
                  className="w-full rounded-lg border-2 border-dashed border-[hsl(var(--input))] hover:border-[hsl(var(--news-red))] bg-[hsl(var(--surface-2))] hover:bg-[hsl(var(--accent))] transition-colors py-5 px-4 flex flex-col items-center gap-2 text-center"
                >
                  <span className="h-10 w-10 rounded-full bg-[hsl(var(--navy))] text-white flex items-center justify-center">
                    <Upload className="h-5 w-5" />
                  </span>
                  <span className="text-sm font-semibold text-[hsl(var(--navy))]">
                    {t("uploadVideo")}
                  </span>
                  <span className="text-xs text-[hsl(var(--muted-foreground))]">video/*</span>
                </button>
              ) : (
                <div
                  data-testid="register-upload-selected"
                  className="w-full rounded-lg border border-[hsl(var(--border))] bg-[hsl(var(--surface-2))] px-4 py-3"
                >
                  <div className="flex items-center gap-3">
                    <span className="h-9 w-9 rounded-lg bg-[hsl(var(--navy))] text-white flex items-center justify-center shrink-0">
                      <FileVideo className="h-4 w-4" />
                    </span>
                    <div className="flex-1 min-w-0 text-start">
                      <p className="text-sm font-semibold text-[hsl(var(--ink))] truncate">
                        {videoFile.name}
                      </p>
                      <p className="text-xs text-[hsl(var(--muted-foreground))]">
                        {formatSize(videoFile.size)}
                      </p>
                    </div>
                    {!uploading && (
                      <button
                        type="button"
                        data-testid="register-remove-file-button"
                        onClick={removeFile}
                        className="text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--news-red))] transition-colors shrink-0"
                        aria-label={t("removeFile")}
                      >
                        <X className="h-4 w-4" />
                      </button>
                    )}
                  </div>
                  {(uploading || uploadProgress > 0) && (
                    <div className="mt-3" data-testid="upload-progress">
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-xs font-semibold text-[hsl(var(--navy))]">
                          {uploading ? t("uploading") : ""}
                        </span>
                        <span className="text-xs font-bold tabular-nums text-[hsl(var(--navy))]">
                          {uploadProgress}%
                        </span>
                      </div>
                      <div className="h-2 rounded-full bg-[hsl(var(--border))] overflow-hidden">
                        <div
                          className="h-full rounded-full bg-[hsl(var(--news-red))] transition-all duration-150"
                          style={{ width: `${uploadProgress}%` }}
                        />
                      </div>
                    </div>
                  )}
                </div>
              )}
              <Input
                id="reg-video"
                data-testid="register-video-input"
                type="url"
                value={form.videoUrl}
                onChange={setInput("videoUrl")}
                placeholder={t("videoPlaceholder")}
                maxLength={300}
                className="h-10 bg-[hsl(var(--surface))] border-[hsl(var(--input))] focus-visible:ring-2 focus-visible:ring-[hsl(var(--news-red))]"
              />
            </div>

            {error && (
              <p data-testid="register-form-error" className="text-sm font-medium text-[hsl(var(--news-red))]">
                {error}
              </p>
            )}

            <div className="flex flex-col-reverse sm:flex-row justify-end gap-3 pt-2">
              <Button
                type="button"
                data-testid="register-cancel-button"
                variant="outline"
                onClick={handleClose}
                disabled={submitting}
                className="border-[hsl(var(--border))] text-[hsl(var(--navy))] hover:bg-[hsl(var(--accent))] h-11 px-6"
              >
                {t("cancel")}
              </Button>
              <Button
                type="submit"
                data-testid="register-submit-button"
                disabled={submitting}
                className="bg-[hsl(var(--news-red))] text-white hover:bg-[hsl(var(--news-red-dark))] active:scale-[0.98] transition-colors h-11 px-6 font-semibold"
              >
                {submitting ? (
                  <Loader2 className="h-4 w-4 me-2 animate-spin" />
                ) : (
                  <UserPlus className="h-4 w-4 me-2" />
                )}
                {uploading ? t("uploading") : submitting ? t("registering") : t("submitRegister")}
              </Button>
            </div>
          </form>
        </div>
      </DialogContent>
    </Dialog>
  );
};
